<script setup lang="ts">
import { provideUseId } from '@headlessui/vue'
provideUseId(() => useId())
const toLocalePath = useLocalePath()

const props = defineProps({
  active: { type: String, required: true },
  user: {
    type: Object as PropType<ReturnType<typeof useAuth>['data']['value']>,
    default: null,
    required: false
  }
})

const { continueScroll, stopScroll } = useScroll()
const { getStudentNavbarLinks } = useStaticData()
const links = getStudentNavbarLinks(props.user)

const homeLink = getHomePage(props.user)

const showNavbarMenu = ref(false)

const handleLogout = () => navigateTo(toLocalePath('/logout'))

watch(showNavbarMenu, () => {
  if (showNavbarMenu.value) stopScroll()
  else continueScroll()
})

const role = getPrimaryUserRole(props.user)

// temporary
// ADMIN
const lmsDomain = useRuntimeConfig().public.lmsDomain
const jobAdminDomain = useRuntimeConfig().public.jobAdminDomain
</script>

<template>
  <header id="student-navbar">
    <div id="student-navbar-content">
      <div id="student-navbar-logo">
        <NuxtLink :to="homeLink" alt="TalentLabs">
          <img src="/images/landing/talentlabs-logo-thicker.svg" alt="" >
        </NuxtLink>
      </div>

      <div v-if="role === 'candidate'" id="student-navbar-links">
        <!-- Admin links -->
        <LayoutNavbarLink
          v-if="isCourseAdmin(user) || isInstructor(user)"
          :link="{
            title: 'Grade assignments',
            link: `${lmsDomain}/classroom/course-admin/course-grading`
          }"
          :active="''"
        />

        <!-- Admin links -->
        <LayoutNavbarLink
          v-if="isCourseAdmin(user) || isTalentAdmin(user)"
          :link="{
            title: 'Manage talents',
            link: `${lmsDomain}/classroom/course-admin/students`
          }"
          :active="''"
        />

        <!-- Admin links -->
        <LayoutNavbarLink
          v-if="isCompanyStaff(user) || isJobAdmin(user)"
          :link="{
            title: 'Manage jobs',
            link: jobAdminDomain
          }"
          :active="''"
        />

        <LayoutNavbarLink
          v-for="link in links"
          :key="`student-link-${link.title}`"
          :link="link"
          :active="active"
        />
      </div>

      <LayoutNavbarLayoutAdminNavbar
        v-else
        :active="active"
        :user="user"
        :show-menu="showNavbarMenu"
        @close-menu="showNavbarMenu = false"
      />

      <div id="student-navbar-btns">
        <LayoutNavbarRightBtns
          :user="user"
          :role="role"
          :show-menu="showNavbarMenu"
          @click-menu="showNavbarMenu = !showNavbarMenu"
          @handle-logout="handleLogout"
        />
      </div>
    </div>

    <div
      v-if="showNavbarMenu && role === 'candidate'"
      id="student-navbar-menu"
      class="anim-fade-down"
    >
      <LayoutNavbarLinkMobile
        :links="links"
        :role="role"
        @close-menu="showNavbarMenu = false"
        @handle-logout="handleLogout"
      />
    </div>
  </header>
</template>

<style scoped lang="scss">
#student-navbar {
  height: var(--navbarHeight);
  /* height: 45px; */
  width: 100vw;
  position: fixed;
  top: 0px;
  left: 0;
  z-index: 100;
  background: white;

  /* $logoWidth: 85px; */
  $logoWidth: var(--sidebarWidth);

  &-content {
    height: 100%;
    display: grid;
    grid-template-columns: $logoWidth 1fr auto;
    align-items: center;
    /* box-shadow: 0 5px 10px rgba(56, 70, 87, 0.1); */
    border-bottom: 1px solid gainsboro;
  }

  &-logo {
    a {
      display: block;
      width: $logoWidth;
      height: 17px;
    }

    img {
      width: 100%;
      height: 100%;
    }
  }

  &-links {
    display: flex;
    margin-left: 10px;
    gap: 7px;
    height: 100%;
  }

  &-btns {
    display: flex;
    align-items: center;
    gap: 20px;
    padding-right: 50px;
  }
}

#student-navbar-menu {
  height: calc(100dvh - var(--navbarHeight));
  background-color: white;
  border-top: 1px solid var(--borderL2);
  overflow-y: scroll;
}

@media (max-width: 1250px) {
  #student-navbar {
    $logoWidth: 100px;

    &-content {
      grid-template-columns: $logoWidth 1fr auto;
    }

    &-logo {
      padding-left: 20px;
      width: $logoWidth;

      a {
        width: 100%;
        height: 30px;
      }
    }

    &-links {
      justify-content: center;
    }

    &-btns {
      padding-right: 20px;
    }
  }
}

@media (max-width: 900px) {
  #student-navbar {
    &-content {
      box-shadow: none;
      padding: 0 20px;
      display: flex;
      justify-content: space-between;
    }

    &-logo {
      width: 90px;
      padding: 0;
    }

    &-links {
      display: none;
    }

    &-btns {
      padding: 0;
      gap: 10px;
    }
  }
}
</style>
